import { ArrowLeft, Heart, MessageCircle, Phone, Mail, Sparkles, Clock, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function SurpriseConnectionGuide() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-rose-700 hover:text-rose-800 hover:bg-rose-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Week
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-rose-100 rounded-full">
              <Heart className="w-8 h-8 text-rose-600" />
            </div>
            <div>
              <h1 className="text-3xl font-serif font-bold text-gray-900">Surprise Connection</h1>
              <p className="text-rose-600">Week 10, Day 2 - Social Connections</p>
            </div>
          </div>

          <div className="prose prose-rose max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              One of the most beautiful gifts you can give someone is unexpected attention. Today, reach out to 
              someone you haven't connected with in a while and brighten their day with a simple message.
            </p>

            <div className="bg-rose-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-rose-800 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Why Surprise Connections Matter
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Maintains friendships that might otherwise fade</li>
                <li>• Reduces feelings of loneliness for both of you</li>
                <li>• Strengthens your social support network</li>
                <li>• Reminds others they matter to you</li>
                <li>• Creates positive ripple effects in your relationships</li>
              </ul>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">Ways to Reach Out</h2>
            
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className="bg-white border-2 border-rose-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <MessageCircle className="w-5 h-5 text-rose-600" />
                  <h3 className="font-semibold text-gray-900">Text Message</h3>
                </div>
                <p className="text-gray-600 text-sm">Quick, easy, and less pressure. Perfect for casual reconnections.</p>
              </div>
              
              <div className="bg-white border-2 border-rose-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Phone className="w-5 h-5 text-rose-600" />
                  <h3 className="font-semibold text-gray-900">Voice Message</h3>
                </div>
                <p className="text-gray-600 text-sm">More personal than text, hearing your voice adds warmth.</p>
              </div>
              
              <div className="bg-white border-2 border-rose-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Phone className="w-5 h-5 text-rose-600" />
                  <h3 className="font-semibold text-gray-900">Phone Call</h3>
                </div>
                <p className="text-gray-600 text-sm">The most personal option for deeper reconnection.</p>
              </div>
              
              <div className="bg-white border-2 border-rose-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Mail className="w-5 h-5 text-rose-600" />
                  <h3 className="font-semibold text-gray-900">Handwritten Note</h3>
                </div>
                <p className="text-gray-600 text-sm">Old-fashioned but incredibly meaningful and memorable.</p>
              </div>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">Message Ideas</h2>
            
            <div className="space-y-4 mb-8">
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-rose-400">
                <p className="text-gray-700 italic">"I was just thinking about you! How have you been?"</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-rose-400">
                <p className="text-gray-700 italic">"Remember when we [shared memory]? That still makes me smile!"</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-rose-400">
                <p className="text-gray-700 italic">"I saw [something] and it reminded me of you. Hope you're doing well!"</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-rose-400">
                <p className="text-gray-700 italic">"How are you really doing? I'd love to catch up sometime."</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-rose-100 to-pink-100 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-rose-800 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Who to Reach Out To
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• A friend from school you've lost touch with</li>
                <li>• A former coworker you enjoyed working with</li>
                <li>• A relative you haven't called in months</li>
                <li>• A neighbor who moved away</li>
                <li>• Someone who helped you in the past</li>
                <li>• A friend going through a difficult time</li>
              </ul>
            </div>

            <div className="bg-amber-50 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-amber-800 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Tips for Success
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Don't overthink it - a simple message is perfect</li>
                <li>• Be genuine and specific when possible</li>
                <li>• Don't expect an immediate response</li>
                <li>• Make it about them, not about needing something</li>
                <li>• Set a reminder to do this weekly</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
